import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import {
  INSTITUTION_EXPECTED_ACTIVITY_DEFAULTS,
  type InstitutionExpectedActivity,
  institutionDetailsExpectedActivityApiToInstitutionExpectedActivity,
  institutionExpectedActivitySchema,
  institutionExpectedActivityToInstitutionDetailsExpectedActivityApi,
} from '@/features/institution/expected-activity/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionExpectedActivityFieldsetAccountPurpose } from './fieldsets/account-purpose';
import { InstitutionExpectedActivityFieldsetIncome } from './fieldsets/income';
import { InstitutionExpectedActivityFieldsetMonthlyActivity } from './fieldsets/monthly-activity';
import { InstitutionExpectedActivityFieldsetSourceOfFunds } from './fieldsets/source-of-funds';

const InstitutionExpectedActivityFormStepExpectedActivityLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.expectedActivity.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.expectedActivity.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionExpectedActivityFormStepExpectedActivityLegend.displayName = 'InstitutionExpectedActivityFormStepExpectedActivityLegend';

const InstitutionExpectedActivityFormStepExpectedActivity = () => (
  <div className="space-y-6">
    <InstitutionExpectedActivityFieldsetIncome />
    <InstitutionExpectedActivityFieldsetMonthlyActivity />
    <InstitutionExpectedActivityFieldsetAccountPurpose />
    <InstitutionExpectedActivityFieldsetSourceOfFunds />
  </div>
);
InstitutionExpectedActivityFormStepExpectedActivity.displayName = 'InstitutionExpectedActivityFormStepExpectedActivity';

const FORM_STEPS = {
  EXPECTED_ACTIVITY: {
    Component: InstitutionExpectedActivityFormStepExpectedActivity,
    Legend: InstitutionExpectedActivityFormStepExpectedActivityLegend,
    schema: institutionExpectedActivitySchema,
  },
} satisfies Record<string, FormStep<InstitutionExpectedActivity>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionExpectedActivityFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionExpectedActivity>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'expectedActivity', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionExpectedActivityFormCurrentStep.displayName = 'InstitutionExpectedActivityFormCurrentStep';

const InstitutionExpectedActivityFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionExpectedActivity>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionExpectedActivityFormCurrentStepLegend.displayName = 'InstitutionExpectedActivityFormCurrentStepLegend';

const InstitutionExpectedActivityFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionExpectedActivity>();
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('expectedActivity');
  const navigate = useNavigate();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(prevDetailsRoute, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, prevDetailsRoute]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionExpectedActivityFormBackButton.displayName = 'InstitutionExpectedActivityFormBackButton';

const InstitutionExpectedActivityFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionExpectedActivity>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionExpectedActivityFormFooter.displayName = 'InstitutionExpectedActivityFormFooter';

const InstitutionExpectedActivityForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'expectedActivity');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('expectedActivity');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionExpectedActivity, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { expectedActivity: await institutionExpectedActivityToInstitutionDetailsExpectedActivityApi(data) };

    await mutateAsync(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
      },
    });
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.expectedActivity
    ? institutionDetailsExpectedActivityApiToInstitutionExpectedActivity(institutionDetailsAnswers.expectedActivity)
    : INSTITUTION_EXPECTED_ACTIVITY_DEFAULTS;

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionExpectedActivityFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionExpectedActivityFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionExpectedActivity) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionExpectedActivityFormCurrentStep />
          <InstitutionExpectedActivityFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionExpectedActivityForm.displayName = 'InstitutionExpectedActivityForm';

export default InstitutionExpectedActivityForm;
